import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest } from 'rxjs';
import { take } from 'rxjs/operators';

import { DeviceId } from '../../../site-detail/models/Device';
import { DeviceStateUpdateExecutionInfo } from '../../../site-detail/models/Execution';
import { Site, SiteId } from '../../../site-detail/models/Site';
import { DEVICE_GATEWAY } from '../../../site-detail/services/DeviceGateway';
import { DeviceActions } from '../../../site-detail/store/actions/device.actions';
import { SiteDevicesActions } from '../../../site-detail/store/actions/site-devices.actions';
import { getSelectedSiteDevices } from '../../../site-detail/store/selectors/devices';
import { getUIDevicesStatesSync } from '../../../site-detail/store/selectors/ui';
import { getSites } from '../../../site-list/store/selectors/sites';
import { useToaster } from '../../composables/use-toaster';
import { UISocketDataMessage, UISocketDeviceStateUpdateState } from '../../models/UISocket';
import { AppState } from '../../store/app-state';
import { SocketAction, SocketActionHandler } from '../SocketActionHandler';

export interface UISocketDeviceStateUpdateMessage extends UISocketDataMessage {
    deviceId: DeviceId;
    siteId: SiteId;
    states: UISocketDeviceStateUpdateState[];
}

@Injectable()
export class DeviceStatesUpdatedHandler implements SocketActionHandler {
    readonly action: SocketAction = 'deviceStatesUpdated';

    private readonly deviceGateway = inject(DEVICE_GATEWAY);
    private readonly store = inject<Store<AppState>>(Store);
    private readonly router = inject(Router);
    private readonly translateService = inject(TranslateService);
    private readonly toaster = useToaster();

    handle(message: UISocketDeviceStateUpdateMessage): void {
        message.states.forEach((state) =>
            this.store.dispatch(
                DeviceActions.deviceUpdateCompleted({
                    deviceId: message.deviceId,
                    value: state.value,
                    stateId: state.name,
                }),
            ),
        );

        this.updateSiteDevice(message.siteId, message.deviceId);

        combineLatest([
            this.store.select(getSites),
            this.store.select(getSelectedSiteDevices),
            this.store.select(getUIDevicesStatesSync),
        ])
            .pipe(take(1))
            .subscribe(([sites, devices, devicesStatesSync]) => {
                const site: Site = sites[message.siteId];
                if (!site) {
                    return;
                }

                const hasDevice = devices.some((device) => device.id === message.deviceId);
                if (!hasDevice) {
                    return;
                }

                this.sendToasterDeviceUpdateStateMessage(site, devicesStatesSync, message);
            });
    }

    private updateSiteDevice(id_site: string, id_device: string) {
        // TODO needs refacto; reload full device to pass through value conversion/transform/... defined in
        //  DeviceDetailComponent:updateDeviceDefinition(), so value is correctly store in store;
        //  refacto must go further by rethink and normalize store AND move all business conversion in
        //  MS Device ; transforms for humans reading purpose are only behaviors that must be kept here
        this.deviceGateway.getDeviceById(id_device).subscribe((newDevice) => {
            this.store.dispatch(SiteDevicesActions.deviceUpdated({ siteId: id_site, device: newDevice }));
        });
    }

    private sendToasterDeviceUpdateStateMessage(
        site: Site,
        devicesStatesSync: DeviceStateUpdateExecutionInfo[],
        message: UISocketDeviceStateUpdateMessage,
    ) {
        const hasUpdatedState = message.states.some((state) =>
            devicesStatesSync.some((job) => job.state === state.name),
        );
        if (!hasUpdatedState) {
            return;
        }

        this.toaster.success({
            title: this.translateService.instant('TOASTER_DEVICE_SETTING_UPDATE_SUCCESS_TITLE'),
            onClick: () =>
                this.router.navigate([`/gateway/${site.gatewayId}`], {
                    queryParams: { deviceId: message.deviceId },
                }),
        });
    }
}
